import { ModalBody, ModalContainer, ModalFooter, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import classNames from 'classnames'

type StatusType = 'success' | 'warning' | 'error' | 'info' | 'neutral'

export interface IAlertModalProps {
  subtitle?: string
  status: StatusType
  title: string
  body: any
  options: {
    text: string
    status: StatusType
    role: ModalDismiss
    callback?: () => void
  }[]
}

export const AlertModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IAlertModalProps
}) => {
  const { subtitle, status, title, body, options } = componentProps

  const SuccessIcon = commonIcons.toastSuccess
  const WarningIcon = commonIcons.toastWarning
  const ErrorIcon = commonIcons.toastError
  const InfoIcon = commonIcons.toastInfo

  const getIcon = () => {
    if (status === 'success') {
      return (
        <div className='bg-success border border-on-success rounded-full p-[2px] ml-2'>
          <SuccessIcon className='text-on-success text-[1em]' />
        </div>
      )
    }
    if (status === 'warning') {
      return (
        <div className='bg-warning border border-on-warning rounded-full p-[2px] ml-2'>
          <WarningIcon className='text-on-warning text-[1em]' />
        </div>
      )
    }
    if (status === 'error') {
      return (
        <div className='bg-error border border-on-error rounded-full p-[2px] ml-2'>
          <ErrorIcon className='text-on-error text-[1em]' />
        </div>
      )
    }
    if (status === 'info') {
      return (
        <div className='bg-info border border-on-info rounded-full p-[2px] ml-2'>
          <InfoIcon className='text-on-info text-[1em]' />
        </div>
      )
    }

    return null
  }

  const getButtonClasses = (status: string) => {
    if (status === 'success') {
      return 'action-success'
    }
    if (status === 'warning') {
      return 'action-warning'
    }
    if (status === 'error') {
      return 'action-error'
    }
    if (status === 'neutral') {
      return 'action-neutral'
    }
    return 'action-info'
  }

  const Icon = getIcon()

  return (
    <ModalContainer innerClasses='!h-auto !min-h-0 !max-w-[400px]'>
      <ModalHeaderContainer>
        <ModalHeader
          subtitle={subtitle}
          subtitleClasses='capitalize'
          title={title}
          titleIcon={Icon}
          titleClasses='flex items-center justify-between w-full'
        />
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[80px] !pb-[100px] sm:!pb-[80px] text-text jost-light'>{body}</ModalBody>
      <ModalFooter>
        <div className='flex items-end justify-start gap-[8px]'>
          {options?.map((o, i) => (
            <button
              key={`alert-option-${i}`}
              type='button'
              aria-label={`Alert ${o.text} option`}
              className={classNames(
                'capitalize text-text rounded-full min-w-[100px] px-[15px] py-[0px] hover:opacity-70',
                getButtonClasses(o.status)
              )}
              onClick={() => {
                if (o.callback) {
                  o.callback()
                }
                onDismiss(null, o.role)
              }}
            >
              {o.text}
            </button>
          ))}
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
